/**
 * Admin → Nav Visibility.
 *
 * Controls which sidebar items are hidden for ALL users. Admin-only.
 * Individual users can still locally hide more (via sidebar edit mode)
 * but can't un-hide something this page hides.
 */
import { useEffect, useMemo, useState } from 'react'
import { EyeOff, Eye, RefreshCw, Save, Search } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import { DEFAULT_GROUPS } from '../components/layout/SideNav'

interface HiddenDetail {
  path: string
  hidden_by: string
  hidden_at: string | null
  note: string
}

export default function NavVisibilityPage() {
  const [hidden, setHidden] = useState<Set<string>>(new Set())
  const [details, setDetails] = useState<Record<string, HiddenDetail>>({})
  const [dirty, setDirty] = useState<Set<string>>(new Set())
  const [query, setQuery] = useState('')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  const refresh = () => {
    setLoading(true)
    api.get('/api/admin/nav/hidden')
      .then(r => {
        setHidden(new Set(r.data?.paths || []))
        setDetails(Object.fromEntries((r.data?.details || []).map((d: HiddenDetail) => [d.path, d])))
        setDirty(new Set(r.data?.paths || []))
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { refresh() }, [])

  const toggle = (path: string) => {
    setDirty(prev => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }

  const changed = useMemo(() => {
    if (hidden.size !== dirty.size) return true
    for (const p of dirty) if (!hidden.has(p)) return true
    return false
  }, [hidden, dirty])

  const save = async () => {
    setSaving(true)
    setMessage(null)
    try {
      await api.put('/api/admin/nav/hidden', { paths: Array.from(dirty) })
      setHidden(new Set(dirty))
      setMessage('Saved. Changes apply to every user on their next page load.')
      setTimeout(() => setMessage(null), 4000)
    } catch (e: any) {
      setMessage(`Save failed: ${e?.response?.data?.detail || 'unknown'}`)
    } finally {
      setSaving(false)
    }
  }

  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return DEFAULT_GROUPS
    return DEFAULT_GROUPS
      .map(g => ({
        ...g,
        items: g.items.filter(i =>
          i.label.toLowerCase().includes(q) ||
          i.to.toLowerCase().includes(q) ||
          (i.keywords || '').toLowerCase().includes(q)
        ),
      }))
      .filter(g => g.items.length > 0)
  }, [query])

  const totalHidden = dirty.size

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <EyeOff size={18} /> Nav Visibility
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          Hide sidebar items for every user · admin only
        </span>
        <button type="button" onClick={refresh} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="card p-4 bg-surface-raised/40 border-border">
        <p className="text-xs text-text-secondary leading-relaxed">
          Tick an item to <span className="text-rose-light font-semibold">hide</span> it from the sidebar for every user.
          Changes apply on their next page-load. Individual users can also locally hide more items via the
          sidebar Edit mode (per-browser), but can't un-hide anything selected here.
        </p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
          <input className="input pl-7 w-full" placeholder="Search nav items"
            value={query} onChange={e => setQuery(e.target.value)} />
        </div>
        <span className="text-[11px] text-text-muted">
          <span className="font-semibold text-rose-light">{totalHidden}</span> hidden
          {changed && <span className="text-amber-light ml-2">· unsaved changes</span>}
        </span>
        <button type="button" onClick={save} disabled={!changed || saving}
          className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-40">
          {saving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
          Save
        </button>
      </div>

      {message && (
        <div className="card p-3 border-emerald/30 bg-emerald/5 text-xs text-emerald-light">
          {message}
        </div>
      )}

      <div className="space-y-3">
        {filteredGroups.map(group => (
          <section key={group.id} className="card p-0">
            <header className="px-3 py-2 border-b border-border flex items-center justify-between">
              <h2 className="text-[11px] uppercase tracking-widest text-text-primary font-semibold">{group.title}</h2>
              <span className="text-[10px] text-text-muted">{group.items.length} items</span>
            </header>
            <ul className="divide-y divide-border">
              {group.items.map(item => {
                const isHidden = dirty.has(item.to)
                const existing = details[item.to]
                const Icon = item.icon
                return (
                  <li key={item.to}
                    className={clsx(
                      'px-3 py-2 flex items-center gap-3 hover:bg-surface-raised/30 cursor-pointer',
                      isHidden && 'bg-rose/5',
                    )}
                    onClick={() => toggle(item.to)}>
                    <input type="checkbox" checked={isHidden}
                      onChange={() => toggle(item.to)}
                      onClick={e => e.stopPropagation()}
                      aria-label={`Hide ${item.label}`} />
                    <Icon size={14} className={clsx('shrink-0', isHidden ? 'text-rose-light' : 'text-text-muted')} />
                    <div className="flex-1 min-w-0">
                      <div className={clsx('text-sm', isHidden ? 'text-rose-light line-through' : 'text-text-primary')}>
                        {item.label}
                      </div>
                      <div className="text-[10px] text-text-muted font-mono">{item.to}</div>
                    </div>
                    {existing && isHidden && (
                      <div className="text-right">
                        <div className="text-[9px] text-text-muted">
                          by {existing.hidden_by || 'system'}
                        </div>
                        <div className="text-[9px] text-text-muted font-mono">
                          {existing.hidden_at?.slice(0, 10)}
                        </div>
                      </div>
                    )}
                    <span className={clsx(
                      'text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border',
                      isHidden
                        ? 'bg-rose/15 text-rose-light border-rose/30'
                        : 'bg-emerald/10 text-emerald border-emerald/30',
                    )}>
                      {isHidden ? <><EyeOff size={9} className="inline" /> Hidden</> : <><Eye size={9} className="inline" /> Visible</>}
                    </span>
                  </li>
                )
              })}
            </ul>
          </section>
        ))}
      </div>
    </div>
  )
}
